import { apiSlice } from "./apiSlice";
import {
  CourseEntity,
  PaginatedCoursesResponse,
} from "../../services/courses/courses.types";

export interface GetCoursesPaginatedArgs {
  page: number;
  pageSize: number;
  id?: number;
  title?: string;
  subject_id?: number;
  program_code?: string;
}

export interface CoursesPage {
  data: CourseEntity[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
  };
}

export const coursesApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getCoursesPaginated: builder.query<CoursesPage, GetCoursesPaginatedArgs>({
      query: ({ page, pageSize, id, title, subject_id, program_code }) => ({
        url: "/courses",
        method: "GET",
        params: {
          page,
          // The courses backend names the page size `limit`; the app-facing
          // arg is `pageSize` (normalized back in transformResponse).
          limit: pageSize,
          ...(id !== undefined && { id }),
          ...(title && { title }),
          ...(subject_id !== undefined && { subject_id }),
          ...(program_code && { program_code }),
        },
      }),
      transformResponse: (response: PaginatedCoursesResponse): CoursesPage => ({
        data: response.data,
        pagination: {
          page: response.pagination.page,
          pageSize: response.pagination.limit,
          total: response.pagination.total,
        },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: "Course" as const,
                id,
              })),
              { type: "Course" as const, id: "LIST" },
            ]
          : [{ type: "Course" as const, id: "LIST" }],
    }),
    deleteCourse: builder.mutation<void, number>({
      query: (id) => ({
        url: `/courses/${id}`,
        method: "DELETE",
      }),
      // Deleted courses land in the trash, so its cache must refresh too.
      // Error guard: a failed delete must not refetch anything.
      invalidatesTags: (result, error) =>
        error ? [] : [{ type: "Course" as const, id: "LIST" }, "Trash" as const],
    }),
    // children_mode 'duplicate': the new course gets independent copies of
    // its lessons and resources; 'link': the copy shares the SAME lessons
    // and resources. The original stays untouched in both modes.
    duplicateCourse: builder.mutation<
      { id: number; title: string },
      { id: number; title: string; children_mode: "duplicate" | "link" }
    >({
      query: ({ id, title, children_mode }) => ({
        url: `/courses/${id}/duplicate`,
        method: "POST",
        data: { title, children_mode },
      }),
      invalidatesTags: (result, error) =>
        error ? [] : [{ type: "Course" as const, id: "LIST" }],
    }),
  }),
});

export const {
  useGetCoursesPaginatedQuery,
  useDeleteCourseMutation,
  useDuplicateCourseMutation,
} = coursesApi;
